import React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Align,
  Container,
  Grid,
  GridSize,
  Link,
  Paper,
  units,
  Typography,
  theme,
} from '@hbf/dsl';
import chunk from 'lodash-es/chunk';
import { SliderWithControls } from 'ha/components/Slider';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

interface FeaturedLinksWrapperProps {
  slightlyGray?: boolean;
  children: React.ReactElement | React.ReactElement[];
}

const useWrapperStyles = makeStyles()({
  slightlyGrayBackground: {
    backgroundColor: '#f8f9fa',
  },
  container: {
    paddingBottom: units.rem(4.5),

    [theme.breakpoints.down('lg')]: {
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
});

export const FeaturedLinksWrapper: React.FC<FeaturedLinksWrapperProps> = ({
  slightlyGray,
  children,
}) => {
  const { classes } = useWrapperStyles();

  return (
    <Grid className={slightlyGray ? classes.slightlyGrayBackground : ''}>
      <Container maxWidth="lg" className={classes.container}>
        {children}
      </Container>
    </Grid>
  );
};

const useSpacerStyles = makeStyles()({
  container: {
    paddingTop: 0,

    [theme.breakpoints.up('lg')]: {
      paddingTop: units.rem(5),
    },
  },
});

export const Spacer: React.FC = ({ children }) => {
  const { classes } = useSpacerStyles();

  return <div className={classes.container}>{children}</div>;
};

const useTitlesStyles = makeStyles()({
  titleWrapper: {
    marginBottom: units.rem(1.25),
    padding: units.txb(3, 1.5, 0),

    [theme.breakpoints.up('lg')]: {
      marginBottom: units.rem(2.875),
      padding: units.txb(0, 1.5, 0),
    },
  },
  title: {
    fontSize: units.rem(1.5),

    [theme.breakpoints.up('lg')]: {
      fontSize: units.rem(1.875),
    },
  },
});

interface FeaturedLinksTitleProps {
  titles: string[];
  align?: Align;
}

export const FeaturedLinksTitle: React.FC<FeaturedLinksTitleProps> = ({
  titles,
  align = 'center',
}) => {
  const { classes } = useTitlesStyles();

  return (
    <div className={classes.titleWrapper}>
      {titles.map(translatedTitle => (
        <Typography.H2
          component="h2"
          align={align}
          className={classes.title}
          key={translatedTitle}
        >
          {translatedTitle}
        </Typography.H2>
      ))}
    </div>
  );
};

const useLinksContentStyles = makeStyles()({
  link: {
    fontSize: units.rem('1'),
    lineHeight: units.rem('1.875'),
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    margin: units.yx(0, 0.75),

    '& span:last-child': {
      fontWeight: 'normal',
    },

    [theme.breakpoints.up('lg')]: {
      fontSize: units.rem('0.9'),
      lineHeight: units.rem('2'),
    },
  },
  smallScreenWrapper: {
    overflowX: 'auto',
    padding: units.rem('0.5'),
  },
  smallScreenCard: {
    width: units.rem('15'),
    height: '100%',
    minHeight: units.rem('17'),
    padding: units.trbl(1, 0, 1.25, 1.5),
    marginRight: units.rem('0.75'),
  },
  largeScreenWrapper: {
    textAlign: 'center',
  },
});

export type FeaturedLink = {
  url: string;
  title: string;
  textContent: string | string[];
};

interface FeaturedLinksContentProps {
  items: FeaturedLink[];
  maxItemsPerColumn: number;
  columnWidth?: GridSize;
}

export const FeaturedLinksContent: React.FC<FeaturedLinksContentProps> = ({
  items,
  maxItemsPerColumn,
}) => {
  const { lg } = useMediaQuery();
  const columns = chunk(items, maxItemsPerColumn);
  const { classes } = useLinksContentStyles();

  if (lg) {
    return (
      <Grid
        item
        container
        spacing={1}
        justifyContent="center"
        className={classes.largeScreenWrapper}
      >
        {columns.map((column, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid item key={`countriesList${index}`}>
            {column.map(row => (
              <div key={row.title}>
                <Link
                  className={classes.link}
                  to={row.url}
                  kind="hover-underline"
                  title={row.title}
                >
                  {Array.isArray(row.textContent)
                    ? row.textContent.map(textContent => (
                        <span key={textContent}>{textContent}</span>
                      ))
                    : row.textContent}
                </Link>
              </div>
            ))}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container wrap="nowrap" className={classes.smallScreenWrapper}>
      <SliderWithControls
        style={{
          width: units.rem(16.25),
          overflowX: 'visible',
        }}
        slideStyle={{
          padding: units.rem(0.5),
        }}
      >
        {columns.map((column, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid item key={index} data-test-locator="Suggestion Group">
            <Paper className={classes.smallScreenCard}>
              {column.map(row => (
                <div key={row.title}>
                  <Link
                    className={classes.link}
                    to={row.url}
                    kind="hover-underline"
                    title={row.title}
                  >
                    {Array.isArray(row.textContent)
                      ? row.textContent.map(textContent => (
                          <span key={textContent}>{textContent}</span>
                        ))
                      : row.textContent}
                  </Link>
                </div>
              ))}
            </Paper>
          </Grid>
        ))}
      </SliderWithControls>
    </Grid>
  );
};
